/**
 * Unit conversion and stock display helpers.
 *
 * All functions accept a `unitCodes` array (from Vuex store or prop)
 * with items shaped as { value, label, group, factor }.
 */

import { globalFunc } from "./global";

// ─── Well-known conversions ──────────────────────────────────
const KNOWN_CONVERSIONS = {
  "KGM:GRM": 1000,
  "GRM:KGM": 0.001,
  "LTR:MLT": 1000,
  "MLT:LTR": 0.001,
};

// ─── "Small → Large" display conversions (GRM→KGM, MLT→LTR) ─
const UP_CONVERSIONS = {
  GRM: { factor: 0.001, target: "KGM" },
  MLT: { factor: 0.001, target: "LTR" },
};

// ─── Piece unit codes ────────────────────────────────────────
const PIECE_CODES = ["H87", "1N"];

/**
 * Find a unit code entry by code string.
 */
export function findUnitCode(unitCodes, code) {
  if (!code || !unitCodes) return null;
  return unitCodes.find((uc) => uc.value === code) || null;
}

/**
 * Get the human-readable label for a unit code.
 * Returns the code itself as fallback.
 */
export function getUnitCodeLabel(unitCodes, code) {
  if (!code) return "";
  const found = findUnitCode(unitCodes, code);
  return found ? found.label : code;
}

/**
 * Get the group of a unit code ('piece', 'mass', 'liter').
 */
export function getUnitGroup(unitCodes, code) {
  if (!code) return null;
  const found = findUnitCode(unitCodes, code);
  return found ? found.group : null;
}

/**
 * Convert an amount from one unit to another.
 * Uses hardcoded conversions first, then falls back to factor-based.
 *
 * @returns {number|null} converted value, or null if impossible
 */
export function convertUnits(amount, fromCode, toCode, unitCodes = []) {
  if (!amount || !fromCode || !toCode) return null;
  if (fromCode === toCode) return amount;

  // Well-known conversions
  const key = `${fromCode}:${toCode}`;
  if (KNOWN_CONVERSIONS[key]) return amount * KNOWN_CONVERSIONS[key];

  // Factor-based (requires unitCodes with group & factor)
  const fromUnit = findUnitCode(unitCodes, fromCode);
  const toUnit = findUnitCode(unitCodes, toCode);
  if (!fromUnit || !toUnit || fromUnit.group !== toUnit.group) return null;
  if (fromUnit.factor && toUnit.factor && toUnit.factor > 0) {
    return amount * (fromUnit.factor / toUnit.factor);
  }

  return null;
}

/**
 * Convert a product piece count to the warehouse stock quantity.
 *
 * Piece/package stock maps directly. Mass/liter stock is calculated from the
 * product's net content and converted to the warehouse stock unit.
 */
export function calculateStockQuantityFromPieces(
  pieceCount,
  stockUnitCode,
  netContent,
  netContentUnitCode,
  unitCodes = []
) {
  const pieces = Number(pieceCount);
  if (!Number.isFinite(pieces) || pieces < 0) return null;
  if (pieces === 0) return 0;

  const stockGroup = getUnitGroup(unitCodes, stockUnitCode);
  if (stockGroup === "piece" || stockGroup === "package") {
    return pieces;
  }

  const content = Number(netContent);
  if (
    !Number.isFinite(content) ||
    content <= 0 ||
    !netContentUnitCode ||
    !stockUnitCode
  ) {
    return null;
  }

  return convertUnits(
    pieces * content,
    netContentUnitCode,
    stockUnitCode,
    unitCodes
  );
}

/**
 * Convert a warehouse stock quantity to a complete product piece count.
 *
 * This mirrors the backend piece calculation: incomplete products are not
 * counted as a full piece.
 */
export function calculatePiecesFromStockQuantity(
  stockQuantity,
  stockUnitCode,
  netContent,
  netContentUnitCode,
  unitCodes = []
) {
  const quantity = Number(stockQuantity);
  if (!Number.isFinite(quantity) || quantity < 0) return null;
  if (quantity === 0) return 0;

  const stockGroup = getUnitGroup(unitCodes, stockUnitCode);
  if (stockGroup === "piece" || stockGroup === "package") {
    return Math.floor(quantity);
  }

  const content = Number(netContent);
  if (
    !Number.isFinite(content) ||
    content <= 0 ||
    !netContentUnitCode ||
    !stockUnitCode
  ) {
    return null;
  }

  const contentQuantity = convertUnits(
    quantity,
    stockUnitCode,
    netContentUnitCode,
    unitCodes
  );

  return contentQuantity === null
    ? null
    : Math.floor(contentQuantity / content);
}

/**
 * Format an amount with its unit in a human-readable way.
 * Converts small units to large (GRM→KGM, MLT→LTR) if applicable.
 *
 * @returns {string|null}  e.g. "≈ 29.75 Kilogramm" or null
 */
export function formatReadableQuantity(amount, unitCode, unitCodes = []) {
  if (!amount || !unitCode) return null;

  const locale = globalFunc.getBrowserLocale();

  let displayAmount = amount;
  let displayUnit = unitCode;

  const conv = UP_CONVERSIONS[unitCode];
  if (conv) {
    displayAmount = amount * conv.factor;
    displayUnit = conv.target;
  }

  const label = getUnitCodeLabel(unitCodes, displayUnit);
  const prefix = Number.isInteger(displayAmount) ? "" : "\u2248 ";
  return `${prefix}${displayAmount.toLocaleString(locale, {
    maximumFractionDigits: 2,
  })} ${label}`;
}

/**
 * Get unit codes compatible with a given ingredient's unit.
 * Mass units → mass + piece; Liter units → liter + piece; Piece → all.
 */
export function getCompatibleUnits(unitCodes, ingredientUnitCode) {
  if (!ingredientUnitCode || !unitCodes) return unitCodes || [];

  const group = getUnitGroup(unitCodes, ingredientUnitCode);
  if (!group || group === "piece") return unitCodes;

  return unitCodes.filter((mc) => mc.group === group || mc.group === "piece");
}

/**
 * Calculate ingredient usage rate.
 * = pivot portion (converted) / product net content
 */
export function computeUsageRate(
  pivotNC,
  pivotUnit,
  productNC,
  productUnit,
  unitCodes = [],
  fallback = null
) {
  pivotNC = parseFloat(pivotNC);
  productNC = parseFloat(productNC);

  if (!pivotNC || pivotNC <= 0 || !pivotUnit) return fallback;

  // Piece-based: if product is also piece, ratio = pivot / product
  // Otherwise pivot quantity IS the rate (e.g. "use 2 bottles")
  if (PIECE_CODES.includes(pivotUnit)) {
    if (PIECE_CODES.includes(productUnit) && productNC > 0) {
      return pivotNC / productNC;
    }
    return pivotNC;
  }

  // Product has no net_content: cannot calculate weight/volume-based rate
  if (!productNC || productNC <= 0) return fallback;

  // Same unit: simple ratio
  if (pivotUnit === productUnit) return pivotNC / productNC;

  // Unit conversion
  const converted = convertUnits(pivotNC, pivotUnit, productUnit, unitCodes);
  if (converted !== null) return converted / productNC;

  return fallback;
}

/**
 * Calculate estimated ingredient price.
 * = price_purchase × usage_rate
 */
export function computeEstimatedPrice(
  pricePurchase,
  usageRate,
  fallback = null
) {
  pricePurchase = parseFloat(pricePurchase);
  const fallbackValue = fallback === null || fallback === undefined || fallback === ''
    ? null
    : parseFloat(fallback);

  if (usageRate === null || !pricePurchase) {
    return Number.isNaN(fallbackValue) ? null : fallbackValue;
  }

  return pricePurchase * usageRate;
}
